import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { select, Store } from '@ngrx/store';
import { addDays } from 'date-fns';
import {
  flatMap as _flatmap,
  get as _get,
  groupBy as _groupBy,
  omit as _omit,
  template as _template
} from 'lodash';
import { from, Observable, of, throwError } from 'rxjs';
import { catchError, mergeMap, take, tap, withLatestFrom } from 'rxjs/operators';

import { env } from '@amfam/environment';
import { Policy } from '@amfam/policy/models';
import { AddressPipe, PrettyPolicyNum } from '@amfam/policy/pipes';
import { AnalyticsFacade, EventAnalyticParams } from '@amfam/shared/analytics';
import { PolicyChangeRequestType } from '@amfam/shared/models';
import {
  ArrayToList,
  PhoneNumberSingleStringFormatterPipe,
  PrettyPhoneNumberPipe,
  TitleCasePipe
} from '@amfam/shared/ui/pipes';
import { userQuery } from '@amfam/shared/user';
import { Agent } from '@amfam/shared/utility/agent';
import { BrandSelectors } from '@amfam/shared/utility/brand';
import { fromRouterActions } from '@amfam/shared/utility/navigation';
import {
  Addressee,
  CommunicationService,
  CopyService,
  InternalEmail,
  MessageDetail,
  TemplatedEmail,
  UtilService
} from '@amfam/shared/utility/shared-services';

import * as fromRoot from '../../core/store';
import { ChangeRequest, ChangerequestInternalEmail, PolicyAnalyticActionPayload } from '../models';
import { ANSRequest } from '../models/change-request-ANS.model';
import { PolicyTransmuteService } from './policy-transmute.service';

@Injectable({
  providedIn: 'root'
})
export class PlchangerequestService {
  constructor(
    private communicationService: CommunicationService,
    private store: Store<fromRoot.RootState>,
    private http: HttpClient,
    private utilService: UtilService,
    private analyticsFacade: AnalyticsFacade,
    private policyTransmuteService: PolicyTransmuteService,
    private copyService: CopyService
  ) {}

  private buildEmailContent(model: ChangeRequest) {
    let emailContent: string;
    const requestedChange = this.policyTransmuteService.getChangeRequestTypeText(
      model.policyChangeType
    );
    switch (model.policyChangeType) {
      case PolicyChangeRequestType.ADDRESS_CHANGE:
        emailContent = this.copyService.getCopy('policy.addressChangeAgentEmail', {
          agentName: model.addressChange?.agentName,
          customerName: model.addressChange?.customerName,
          policyNumbers: model.addressChange?.policyNumbers,
          existingAddress: model.addressChange?.existingAddress,
          newAddress: model.addressChange?.newAddress,
          emailAddress: model.addressChange?.emailAddress,
          phoneNumbers: model.addressChange.phoneNumbers,
          requestedChange,
          sendDocuments: model.addressChange?.sendDocuments,
          vehiclesGaraged: model.addressChange?.vehiclesGaraged,
          vehicles: model.addressChange?.vehicles,
          reason: model.addressChange?.reason
        });
        break;

      case PolicyChangeRequestType.VIN_CHANGE:
        emailContent = this.copyService.getCopy('policy.vinChange.agentEmail.body', {
          agentName: model.requestChange?.agentName,
          customerName: model.requestChange?.customerName,
          policyNumbers: model.requestChange?.policyNumbers,
          mailingAddress: model.requestChange?.mailingAddress,
          phoneNumbers: model.requestChange.phoneNumbers,
          emailAddress: model.requestChange?.emailAddress,
          requestedChange,
          whyUpdateVin: model.vinChange.whyUpdateVin,
          affectedVehicle: model.vinChange?.vehicle,
          currentVin: model.vinChange?.currentVin,
          currentYear: model.vinChange.currentYear,
          newVin: model.vinChange.newVin,
          newYear: model.vinChange.newYear,
          make: model.vinChange.make || 'N/A',
          model: model.vinChange.model || 'N/A',
          series: model.vinChange.series || 'N/A',
          reason: model.vinChange?.reason
        });
        break;

      default:
        break;
    }
    return emailContent;
  }

  private buildANSContent(model: ChangeRequest) {
    let ansContent: string;
    const requestedChange = this.policyTransmuteService.getChangeRequestTypeText(
      model.policyChangeType
    );
    switch (model.policyChangeType) {
      case PolicyChangeRequestType.ADDRESS_CHANGE:
        ansContent = this.copyService.getCopy('policy.addressChangeANSMessage', {
          agentName: model.addressChange?.agentName,
          customerName: model.addressChange?.customerName,
          policyNumbers: model.addressChange?.policyNumbers,
          existingAddress: model.addressChange?.existingAddress,
          newAddress: model.addressChange?.newAddress,
          emailAddress: model.addressChange?.emailAddress,
          phoneNumbers: model.addressChange.phoneNumbers,
          requestedChange,
          sendDocuments: model.addressChange?.sendDocuments,
          vehiclesGaraged: model.addressChange?.vehiclesGaraged,
          vehicles: model.addressChange?.vehicles,
          reason: model.addressChange?.reason
        });
        break;
      case PolicyChangeRequestType.DRIVER_LICENSE_CHANGE:
        ansContent = this.copyService.getCopy('policy.driverLicenseChangeANSMessage', {
          agentName: model.driverLicenseChange?.agentName,
          customerName: model.driverLicenseChange?.customerName,
          existingAddress: model.driverLicenseChange?.mailingAdrress,
          phoneNumbers: model.driverLicenseChange.phoneNumbers,
          emailAddress: model.driverLicenseChange?.emailAddress,
          requestedChange,
          driver: model.driverLicenseChange.driver,
          licenseNumber: model.driverLicenseChange.licenseNumber,
          state: model.driverLicenseChange.state,
          reason: model.driverLicenseChange?.reason
        });
        break;
      case PolicyChangeRequestType.VIN_CHANGE:
        ansContent = this.copyService.getCopy('policy.vinChange.agentANS', {
          agentName: model.requestChange?.agentName,
          customerName: model.requestChange?.customerName,
          policyNumbers: model.requestChange?.policyNumbers,
          mailingAddress: model.requestChange?.mailingAddress,
          phoneNumbers: model.requestChange.phoneNumbers,
          emailAddress: model.requestChange?.emailAddress,
          requestedChange,
          whyUpdateVin: model.vinChange.whyUpdateVin,
          affectedVehicle: model.vinChange?.vehicle,
          currentVin: model.vinChange?.currentVin,
          currentYear: model.vinChange.currentYear,
          newVin: model.vinChange.newVin,
          newYear: model.vinChange.newYear,
          make: model.vinChange.make || 'N/A',
          model: model.vinChange.model || 'N/A',
          series: model.vinChange.series || 'N/A',
          reason: model.vinChange?.reason
        });
        break;

      default:
        break;
    }
    return ansContent;
  }

  public sendAgentEmail(model: ChangeRequest) {
    try {
      const emailContent = this.buildEmailContent(model);
      const agentEmail = model.requestChange?.agencyEmail || model.requestChange?.agentEmail;
      const internalAgentEmail = model.requestChange?.agencyEmail
        ? env.policyGracePeriodNotificatonInternalAgencyMailbox
        : env.policyGracePeriodNotificatonInternal;
      const emailAddress: string = env.production ? agentEmail : internalAgentEmail;
      // If we do not have the agent email throw an error
      if (!emailAddress) {
        throwError(() => new Error('Agent Email not found'));
      }
      let email: InternalEmail = new InternalEmail();
      email = Object.assign({}, email, {
        messageFrom: new Addressee(env.policyChangeRequestInternal, 'Policy Change Request'),
        messageToList: [new Addressee(emailAddress, model.requestChange?.agentName)],
        messageSubject: this.copyService.getCopy('policy.addressChange.agentEmail.subject'),
        messageContent: emailContent
      });
      return this.communicationService.sendInternalEmail(email);
    } catch (error) {}
  }

  sendANS(model: ChangeRequest) {
    try {
      const ansContent = this.buildANSContent(model);
      const ansRequest: ANSRequest = {
        notificationsRequest: {
          notificationText: ansContent,
          notificationType: 'ANSMYREQCH',
          customerName: model.requestChange.customerName,
          referenceNumber: model.requestChange.policyNumbers || 'NA',
          formattedReferenceNumber: model.requestChange.policyNumbers || 'NA',
          policyNumber: model.requestChange.policyNumbers,
          from: 'DMSI',
          dueDate: addDays(new Date(), 90).toISOString(),
          createdBy: 'DMSI',
          uniqueId: this.utilService.generateId(),
          sourceSystem: 'MYACCOUNT'
        },
        producerId: model.requestChange.agentId
      };
      return this.ansNotification(ansRequest);
    } catch (error) {}
  }

  sendCustomerEmail(model: ChangeRequest) {
    const messageDetails: MessageDetail[] = new Array<MessageDetail>();
    let email;
    messageDetails.push(new MessageDetail('FirstName', model.requestChange?.customerFirstName));
    messageDetails.push(new MessageDetail('EmailAddress', model.requestChange?.emailAddress));
    messageDetails.push(new MessageDetail('AgentID', String(model.requestChange?.agentId)));
    switch (model.policyChangeType) {
      case PolicyChangeRequestType.ADDRESS_CHANGE:
        messageDetails.push(
          new MessageDetail('ChangeField1', model.addressChange?.existingAddress)
        );
        messageDetails.push(new MessageDetail('ChangeField2', model.addressChange?.newAddress));
        messageDetails.push(new MessageDetail('ChangeDetails', model.addressChange?.reason));
        break;

      case PolicyChangeRequestType.DRIVER_LICENSE_CHANGE:
        //TODO Needs to check with BA whether need to pass the old DL in customer email.
        messageDetails.push(new MessageDetail('ChangeField1', ''));
        messageDetails.push(
          new MessageDetail('ChangeField2', model.driverLicenseChange?.licenseNumber)
        );
        messageDetails.push(new MessageDetail('ChangeDetails', model.driverLicenseChange?.reason));
        break;

      case PolicyChangeRequestType.VIN_CHANGE:
        messageDetails.push(new MessageDetail('ChangeField1', model.vinChange?.currentVin));
        messageDetails.push(new MessageDetail('ChangeField2', model.vinChange?.newVin));
        messageDetails.push(new MessageDetail('ChangeDetails', model.vinChange?.reason));
        break;
    }
    email = new TemplatedEmail(
      'MRTC',
      model.requestChange.partnerId,
      'MyAccount',
      'EN',
      model.requestChange?.customerId,
      model.requestChange?.emailAddress,
      messageDetails
    );
    return this.communicationService.sendTemplatedEmail(email);
  }

  /**
   * @description:This function builds and sends the email notification to the agents.
   * @param changeRequest Object
   * @returns Observable of type any
   */
  public sendAgentEmailNotifications(changeModel: ChangeRequest): Observable<any> {
    const model = new ChangerequestInternalEmail(changeModel);
    const policyList = model.selectedPolicyNumber
      ? model.policyList.filter((p: Policy) => p.policyNumber === model.selectedPolicyNumber)
      : model.policyList;
    const policyGroup: { [id: number]: any[] } = _groupBy(policyList, p =>
      _get(p, 'assignedProducer.producerIdNum')
    );

    return from(Object.keys(policyGroup)).pipe(
      mergeMap(key => this.store.select(fromRoot.selectedAgentFromStoreFactory(key))),
      mergeMap(producer => {
        const policies: Policy[] = policyGroup[producer.id];
        if (policies.length > 0) {
          return this.buildAgentEmailContent(model, producer, policies).pipe(
            mergeMap(emailContent => this.communicationService.sendInternalEmail(emailContent))
          );
        }
      }),
      catchError(error => throwError(error))
    );
  }

  /**
   * @author Abhishek Singh
   * @param
   * @returns
   * @description
   */
  private buildAgentEmailContent(
    model: ChangerequestInternalEmail,
    producer: Agent,
    policies: Policy[]
  ): Observable<InternalEmail> {
    return this.buildAgentEmailSubject(model, producer, policies).pipe(
      withLatestFrom(this.store.select(userQuery.selectFirstName)),
      withLatestFrom(this.store.select(userQuery.selectLastName)),
      mergeMap(([[emailSubjectContent, firstName], lastName]) => {
        const agentEmail = _get(producer, 'agencyEmails[0]', _get(producer, 'emails[0]'));

        const internalAgentEmail = _get(producer, 'agencyEmails[0]')
          ? env.policyGracePeriodNotificatonInternalAgencyMailbox
          : env.policyGracePeriodNotificatonInternal;

        const emailAddress: string = env.production ? agentEmail : internalAgentEmail;

        // If we do not have the agent email throw an error
        if (!emailAddress) {
          throwError({ message: 'Agent Email not found' });
        }
        let email: InternalEmail = new InternalEmail();
        email = Object.assign({}, email, {
          messageFrom: new Addressee(env.policyChangeRequestInternal, 'Policy Change Request'),
          messageToList: [
            new Addressee(emailAddress, `${producer.firstName} ${producer.lastName}`)
          ],
          messageSubject: `Your Customer Has Requested a Change in My Account`,
          messageContent: emailSubjectContent
        });
        return of(email);
      }),
      catchError(error => throwError(error))
    );
  }

  /**
   * @author Abhishek Singh
   * @param
   * @returns
   * @description
   */
  private buildAgentEmailSubject(
    model: ChangerequestInternalEmail,
    producer: Agent,
    policies: Policy[]
  ): Observable<string> {
    return this.store.pipe(
      select(userQuery.selectPartyDetails),
      withLatestFrom(this.store.select(userQuery.selectAddress)),
      take(1),
      mergeMap(([partyDetails, primaryAddress]) => {
        let agentContent: string;
        switch (model.policyChangeType) {
          case PolicyChangeRequestType.NAME_CHANGE:
            agentContent = this.copyService.getCopy('policy.policyNameChangeAgentEmail', {
              agentName: `${producer.firstName} ${producer.lastName}`,
              existingName: `${_get(partyDetails, 'firstName')} ${_get(partyDetails, 'lastName')}`,
              newName: `${new TitleCasePipe().transform(
                model.firstName
              )} ${new TitleCasePipe().transform(model.lastName)}`,
              mailingAddress: `${new AddressPipe().transform(primaryAddress, 'long-singleline')}`,
              emailAddress: _get(partyDetails, 'primaryEmail.emailAddress'),
              phoneNumber: new PhoneNumberSingleStringFormatterPipe().transform(
                _get(partyDetails, 'primaryPhone.tenDigitPhoneNumber')
              ),
              notes: model.comment
            });
            break;

          case PolicyChangeRequestType.ADDRESS_CHANGE:
            agentContent = this.copyService.getCopy('policy.policyAddressChangeAgentEmail', {
              agentName: `${producer.firstName} ${producer.lastName}`,
              existingName: `${_get(partyDetails, 'firstName')} ${_get(partyDetails, 'lastName')}`,
              existingAddress: `${new AddressPipe().transform(primaryAddress, 'long-singleline')}`,
              newMailingAddress: `${new AddressPipe().transform(model.address, 'long-singleline')}`,
              emailAddress: _get(partyDetails, 'primaryEmail.emailAddress'),
              phoneNumber: new PhoneNumberSingleStringFormatterPipe().transform(
                _get(partyDetails, 'primaryPhone.tenDigitPhoneNumber')
              ),
              notes: model.comment
            });
            break;

          case PolicyChangeRequestType.POLICY_COVERAGE_CHANGE:
            agentContent = this.copyService.getCopy('policy.policyCoverageChangeAgentEmail', {
              agentName: `${producer.firstName} ${producer.lastName}`,
              existingName: `${_get(partyDetails, 'firstName')} ${_get(partyDetails, 'lastName')}`,
              mailingAddress: `${new AddressPipe().transform(primaryAddress, 'long-singleline')}`,
              emailAddress: _get(partyDetails, 'primaryEmail.emailAddress'),
              phoneNumber: new PhoneNumberSingleStringFormatterPipe().transform(
                _get(partyDetails, 'primaryPhone.tenDigitPhoneNumber')
              ),
              policytext: _get(policies, 'length', 0) > 1 ? 'Policies' : 'Policy',
              policiesWithRiskDescription: new ArrayToList().transform(
                _flatmap(model.policyList, (policy: Policy) => {
                  if (policy.assignedProducer.producerIdNum === producer.id) {
                    const riskDescription = `<br> Risk Description: ${new ArrayToList().transform(
                      policy.riskDescriptionList
                    )} <br>
                    Policy Number: ${new PrettyPolicyNum().transform(policy)}`;
                    return riskDescription;
                  }
                  return;
                }),
                '<br>'
              ),
              notes: model.comment
            });
            break;

          case PolicyChangeRequestType.MORTGAGE_LENDER_DETAILS_CHANGE:
            let lenderDetails = `<br><b>Requested change:</b> ${model.requestedChange}`;
            let contactDetails = '';
            if (_get(partyDetails, 'primaryPhone.tenDigitPhoneNumber')) {
              contactDetails += `<br><b>Customer phone:</b> ${new PhoneNumberSingleStringFormatterPipe().transform(
                _get(partyDetails, 'primaryPhone.tenDigitPhoneNumber')
              )}`;
            }
            if (_get(partyDetails, 'primaryEmail.emailAddress')) {
              contactDetails += `<br><b>Customer email address:</b> ${_get(
                partyDetails,
                'primaryEmail.emailAddress'
              )}`;
            }
            if (model.currentLenderName)
              lenderDetails += `<br><b>Current lender name:</b> ${model.currentLenderName}`;
            if (model.newLenderName || model.addLenderName)
              lenderDetails += `<br><b>New lender name:</b> ${
                model.newLenderName || model.addLenderName
              }`;
            if (model.lenderToBeRemoved)
              lenderDetails += `<br><b>Lender to be removed:</b> ${model.lenderToBeRemoved}`;
            if (model.comment) lenderDetails += `<br><b>Comment:</b> ${model.comment}`;

            agentContent = this.copyService.getCopy('policy.policyMortgageLenderChangeAgentEmail', {
              agentName: `${producer.firstName}`,
              existingName: `${_get(partyDetails, 'firstName')} ${_get(partyDetails, 'lastName')}`,
              policyNumber: model.selectedPolicyNumber,
              contactDetails,
              lenderDetails
            });
            break;
          default:
            break;
        }

        return of(agentContent);
      }),
      catchError(error => {
        return of('');
      })
    );
  }

  /**
   * @author Abhishek Singh
   * @param
   * @returns
   * @description
   */
  public generateCustomerEmail(changeRequest: ChangeRequest): Observable<any> {
    const model = new ChangerequestInternalEmail(changeRequest);
    const policyList = model.selectedPolicyNumber
      ? model.policyList.filter((p: Policy) => p.policyNumber === model.selectedPolicyNumber)
      : model.policyList;
    const policyGroup: { [id: number]: any[] } = _groupBy(policyList, p =>
      _get(p, 'assignedProducer.producerIdNum')
    );
    const messageDetails: MessageDetail[] = new Array<MessageDetail>();

    return from(Object.keys(policyGroup)).pipe(
      mergeMap(key => this.store.select(fromRoot.selectedAgentFromStoreFactory(key))),
      withLatestFrom(this.store.select(BrandSelectors.selectPartnerId)),
      withLatestFrom(this.store.select(userQuery.selectEmailAddress)),
      withLatestFrom(this.store.select(userQuery.selectCustomerId)),
      withLatestFrom(this.store.select(userQuery.selectFirstName)),
      withLatestFrom(this.store.select(userQuery.selectPartyDetails)),
      withLatestFrom(this.store.select(userQuery.selectAddress)),

      mergeMap(
        ([
          [[[[[agent, partnerIdStore], userEmailAddress], customerId], firstName], partyDetails],
          primaryAddress
        ]) => {
          switch (model.policyChangeType) {
            case PolicyChangeRequestType.NAME_CHANGE:
              messageDetails.push(
                new MessageDetail(
                  'ChangeField1',
                  `${_get(partyDetails, 'firstName')} ${_get(partyDetails, 'lastName')}`
                )
              );
              messageDetails.push(
                new MessageDetail(
                  'ChangeField2',
                  `${new TitleCasePipe().transform(
                    model.firstName
                  )} ${new TitleCasePipe().transform(model.lastName)}`
                )
              );
              messageDetails.push(new MessageDetail('ChangeDetails', model.comment));
              break;
            case PolicyChangeRequestType.ADDRESS_CHANGE:
              messageDetails.push(
                new MessageDetail(
                  'ChangeField1',
                  `${new AddressPipe().transform(primaryAddress, 'long-singleline')}`
                )
              );
              messageDetails.push(
                new MessageDetail(
                  'ChangeField2',
                  `${new AddressPipe().transform(model.address, 'long-singleline')}`
                )
              );
              messageDetails.push(new MessageDetail('ChangeDetails', model.comment));
              break;
            case PolicyChangeRequestType.POLICY_COVERAGE_CHANGE:
              const policiesWithRiskDescription = model.policyList
                .map(risk => {
                  return ` ${new ArrayToList().transform(risk.riskDescriptionList)}`;
                })
                .join(',');

              messageDetails.push(new MessageDetail('ChangeField2', policiesWithRiskDescription));
              messageDetails.push(new MessageDetail('ChangeDetails', model.comment));
              break;
            case PolicyChangeRequestType.MORTGAGE_LENDER_DETAILS_CHANGE:
              let coverageChanges = '';
              if (model.newLenderName || model.addLenderName)
                coverageChanges = `New mortgage lender name: ${
                  model.newLenderName || model.addLenderName
                }`;
              if (model.lenderToBeRemoved)
                coverageChanges = `Mortgage lender removed from policy: ${model.lenderToBeRemoved}`;

              messageDetails.push(
                new MessageDetail(
                  'PolicyNumber',
                  model.selectedPolicyNumber
                    ? this.utilService.maskPolicyNumber(model.selectedPolicyNumber)
                    : ''
                )
              );
              messageDetails.push(new MessageDetail('ChangeType', model.requestedChange));
              messageDetails.push(new MessageDetail('ChangeDetails', model.comment));
              messageDetails.push(new MessageDetail('CoverageChanges', coverageChanges));
              break;
          }

          messageDetails.push(new MessageDetail('FirstName', changeRequest.firstName || firstName));
          messageDetails.push(new MessageDetail('EmailAddress', userEmailAddress));
          messageDetails.push(new MessageDetail('AgentID', agent.agentId.toString()));

          const email = new TemplatedEmail(
            model.policyChangeType === PolicyChangeRequestType.MORTGAGE_LENDER_DETAILS_CHANGE
              ? 'MYAC_TPI'
              : 'MRTC',
            partnerIdStore,
            'MyAccount',
            'EN',
            customerId,
            userEmailAddress,
            messageDetails
          );
          return this.communicationService.sendTemplatedEmail(email);
        }
      ),
      tap(() =>
        this.store.dispatch(
          fromRouterActions.Go({
            path: ['/policies/changerequest/confirmation'],
            extras: { state: { agentId: changeRequest.addressChange?.agentId } }
          })
        )
      ),
      catchError(() =>
        of(
          this.store.dispatch(
            fromRouterActions.Go({
              path: ['/policies/changerequest/confirmation'],
              extras: { state: { agentId: changeRequest.addressChange?.agentId } }
            })
          )
        )
      )
    );
  }

  /**
   * @author Abhishek Singh
   * @param
   * @returns
   * @description
   */
  public sendANSNotifications(changeModel: ChangeRequest): Observable<any> {
    const model = new ChangerequestInternalEmail(changeModel);
    const policyList = model.selectedPolicyNumber
      ? model.policyList.filter((p: Policy) => p.policyNumber === model.selectedPolicyNumber)
      : model.policyList;
    const policyGroup: { [id: number]: any[] } = _groupBy(policyList, p =>
      _get(p, 'assignedProducer.producerIdNum')
    );

    return from(Object.keys(policyGroup)).pipe(
      mergeMap(key => this.store.select(fromRoot.selectedAgentFromStoreFactory(key))),
      mergeMap(producer =>
        this.buildNotificationData(model, String(producer.id), changeModel.policyList)
      ),
      mergeMap(ansRequest => this.ansNotification(ansRequest)),
      catchError(error => throwError(error))
    );
  }

  /**
   * @author Abhishek Singh
   * @param
   * @returns
   * @description
   */
  public ansNotification(model: ANSRequest): Observable<any> {
    const uri = env.producerApi + 'producers/' + model.producerId + '/notifications';
    return this.http.post(uri, _omit(model, ['producerId']));
  }

  /**
   * @author Abhishek Singh
   * @param producerId: Id of the currently selected agent
   * @returns Observable of ANSRequest
   * @description:
   */
  public buildNotificationData(
    model: ChangerequestInternalEmail,
    producerId: string,
    policies: Policy[]
  ): Observable<ANSRequest> {
    return this.store.select(fromRoot.getAgentInformationByProducerId(producerId)).pipe(
      mergeMap(agent => this.buildANSContentBody(model, agent, policies)),
      withLatestFrom(this.store.select(userQuery.selectDisplayName)),
      take(1),
      mergeMap(([ansNotificationBody, displayName]) => {
        const ansNotificationPayload: ANSRequest = {
          notificationsRequest: {
            notificationText: ansNotificationBody,
            notificationType: 'ANSMYREQCH',
            customerName: displayName,
            referenceNumber: model.selectedPolicyNumber || 'NA',
            formattedReferenceNumber: model.selectedPolicyNumber || 'NA',
            policyNumber: '',
            from: 'DMSI',
            dueDate: addDays(new Date(), 90).toISOString(),
            createdBy: 'DMSI',
            uniqueId: this.utilService.generateId(),
            sourceSystem: 'MYACCOUNT'
          },
          producerId: producerId
        };
        return of(ansNotificationPayload);
      }),
      catchError(error => throwError(error))
    );
  }

  /**
   * @author Abhishek Singh
   * @param
   * @returns
   * @description
   */
  private buildANSContentBody(
    model: ChangerequestInternalEmail,
    agent: Agent,
    policies: Policy[]
  ): Observable<string> {
    return this.store.pipe(
      select(userQuery.selectPartyDetails),
      withLatestFrom(this.store.select(userQuery.selectAddress)),
      take(1),
      mergeMap(([partyDetails, primaryAddress]) => {
        const primaryEmail = partyDetails.emails.find(email => email.primaryIndicator);
        const primaryPhone = partyDetails.phones.find(phone => phone.primaryIndicator);
        let agentContent: string;
        switch (model.policyChangeType) {
          case PolicyChangeRequestType.NAME_CHANGE:
            agentContent = this.copyService.getCopy('policy.policyNameChangeANSMessage', {
              agentName: `${agent.fullName}`,
              existingName: `${_get(partyDetails, 'firstName')} ${_get(partyDetails, 'lastName')}`,
              newName: `${model.firstName} ${model.lastName}`,
              mailingAddress: `${new AddressPipe().transform(primaryAddress, 'long-singleline')}`,
              emailAddress: primaryEmail.emailAddress,
              phoneNumber: primaryPhone
                ? new PrettyPhoneNumberPipe().transform(primaryPhone.tenDigitPhoneNumber)
                : ' ',
              notes: model.comment
            });
            break;

          case PolicyChangeRequestType.ADDRESS_CHANGE:
            agentContent = this.copyService.getCopy('policy.policyAddressChangeANSMessage', {
              agentName: `${agent.fullName}`,
              existingName: `${_get(partyDetails, 'firstName')} ${_get(partyDetails, 'lastName')}`,
              existingMailingAddress: `${new AddressPipe().transform(
                primaryAddress,
                'long-singleline'
              )}`,
              newMailingAddress: `${new AddressPipe().transform(model.address, 'long-singleline')}`,
              emailAddress: primaryEmail.emailAddress,
              phoneNumber: primaryPhone
                ? new PrettyPhoneNumberPipe().transform(primaryPhone.tenDigitPhoneNumber)
                : ' ',
              notes: model.comment
            });
            break;

          case PolicyChangeRequestType.POLICY_COVERAGE_CHANGE:
            agentContent = this.copyService.getCopy('policy.policyCoverageChangeANSMessage', {
              agentName: `${agent.fullName}`,
              existingName: `${_get(partyDetails, 'firstName')} ${_get(partyDetails, 'lastName')}`,
              mailingAddress: `${new AddressPipe().transform(primaryAddress, 'long-singleline')}`,
              emailAddress: primaryEmail.emailAddress,
              phoneNumber: primaryPhone
                ? new PrettyPhoneNumberPipe().transform(primaryPhone.tenDigitPhoneNumber)
                : ' ',
              policyText: _get(policies, 'length', 0) > 1 ? 'Policies' : 'Policy',
              policiesWithRiskDescription: new ArrayToList().transform(
                _flatmap(
                  model.policyList,
                  (policy: Policy) =>
                    `${policy.policyNumber} - ${new ArrayToList().transform(
                      policy.riskDescriptionList
                    )}`
                )
              ),
              notes: model.comment
            });
            break;

          case PolicyChangeRequestType.MORTGAGE_LENDER_DETAILS_CHANGE:
            let lenderDetails = `\nRequested change: ${model.requestedChange}`;
            let contactDetails = '';
            if (primaryPhone && primaryPhone.tenDigitPhoneNumber) {
              contactDetails += `\nCustomer phone: ${new PrettyPhoneNumberPipe().transform(
                primaryPhone.tenDigitPhoneNumber
              )}`;
            }
            if (primaryEmail.emailAddress) {
              contactDetails += `\nCustomer email address: ${primaryEmail.emailAddress}`;
            }
            if (model.currentLenderName)
              lenderDetails += `\nCurrent lender name: ${model.currentLenderName}`;
            if (model.newLenderName || model.addLenderName)
              lenderDetails += `\nNew lender name: ${model.newLenderName || model.addLenderName}`;
            if (model.lenderToBeRemoved)
              lenderDetails += `\nLender to be removed: ${model.lenderToBeRemoved}`;
            if (model.comment) lenderDetails += `\n\nComment: ${model.comment}`;

            agentContent = this.copyService.getCopy('policy.policyMortgageLenderChangeANSMessage', {
              agentName: `${agent.firstName}`,
              existingName: `${_get(partyDetails, 'firstName')} ${_get(partyDetails, 'lastName')}`,
              policyNumber: model.selectedPolicyNumber,
              contactDetails,
              lenderDetails
            });
            break;

          default:
            break;
        }

        return of(agentContent);
      }),
      catchError(error => {
        return of('');
      })
    );
  }

  /**
   * @author Abhishek Singh
   * @param
   * @returns
   * @description
   */
  public sendAnalytic(analyticPayload: PolicyAnalyticActionPayload): void {
    let pageAnalytic = analyticPayload.pageAnalytic;
    if (pageAnalytic) {
      const pageName: string = !isNaN(analyticPayload.changeTypeSelected)
        ? _template(analyticPayload.pageAnalytic.pageName)({
            changeTypeSelected:
              ':' +
              this.policyTransmuteService
                .getChangeRequestTypeText(analyticPayload.changeTypeSelected)
                .replace(/[ \/]/g, '')
          })
        : _template(analyticPayload.pageAnalytic.pageName)({
            changeTypeSelected: ''
          });

      pageAnalytic = Object.assign({}, analyticPayload.pageAnalytic, { pageName: pageName });

      if (analyticPayload.policyPageName) {
        pageAnalytic = Object.assign({}, analyticPayload.pageAnalytic, {
          pageName:
            _template(analyticPayload.pageAnalytic.pageName)({
              changeTypeSelected: ''
            }) +
            ':' +
            analyticPayload.policyPageName
        });
      }
    }

    if (analyticPayload.eventAnalytic) {
      let eventAnalytic: EventAnalyticParams = analyticPayload.eventAnalytic;
      if (analyticPayload.policyPageName) {
        eventAnalytic = Object.assign({}, eventAnalytic, {
          eventStep: analyticPayload.policyPageName.toLowerCase()
        });
      }
      return this.analyticsFacade.trackPageAndEvent(pageAnalytic, eventAnalytic);
    }
    return this.analyticsFacade.trackPage(pageAnalytic);
  }
}
